import { PolicyMrnToId, PolicyMrnToURIEncodedId } from "~/lib/mrn";
import type { GetPlatformPoliciesQueryVariables } from "~/operations";
import {
  OrderDirection,
  PolicyAction,
  PolicyAnalyticsOrderField,
  useGetPlatformPoliciesQuery,
} from "~/operations";
import type { GridColDef, GridRowsProp, GridSortModel } from "@mui/x-data-grid";
import { useCallback, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { PlatformPoliciesTableNameCell } from "../components/PlatformPoliciesTable/PlatformPoliciesTableNameCell";
import { PlatformPoliciesTableComplianceCell } from "../components/PlatformPoliciesTable/PlatformPoliciesTableComplianceCell";
import { PlatformPoliciesTableStatusCell } from "../components/PlatformPoliciesTable/PlatformPoliciesTableStatusCell";
import { useDataGridPagination } from "~/hooks";

export interface PlatformPoliciesRow {
  id: string;
  encodedId: string;
  mrn: string;
  name: string;
  icon: string;
  action: PolicyAction;
  assets: number;
  policyCompliance: number | null;
  findingStatsPass: number | null;
  findingStatsTotal: number | null;
}

const SORT_FIELD_MAP: Record<string, PolicyAnalyticsOrderField> = {
  name: PolicyAnalyticsOrderField.Title,
  assets: PolicyAnalyticsOrderField.AssetCount,
  policyCompliance: PolicyAnalyticsOrderField.Completion,
};

const DEFAULT_SORT_MODEL: GridSortModel = [{ field: "name", sort: "asc" }];

export function usePlatformPoliciesTableData() {
  const [sortModel, setSortModel] = useState<GridSortModel>(DEFAULT_SORT_MODEL);

  const columns: GridColDef<PlatformPoliciesRow>[] = useMemo(
    () => [
      {
        field: "name",
        headerName: "Policy",
        flex: 4,
        sortable: true,
        renderCell: PlatformPoliciesTableNameCell,
      },
      {
        field: "action",
        headerName: "Status",
        flex: 1,
        sortable: false,
        renderCell: PlatformPoliciesTableStatusCell,
      },
      {
        field: "assets",
        headerName: "Assets",
        flex: 1,
        sortable: true,
      },
      {
        field: "policyCompliance",
        headerName: "Policy Compliance",
        flex: 2,
        sortable: true,
        renderCell: PlatformPoliciesTableComplianceCell,
      },
    ],
    [],
  );

  const [searchParams] = useSearchParams();
  const queryTerm = searchParams.get("queryTerm");

  const filterVariables = useMemo(() => {
    return {
      ...(queryTerm?.length ? { query: queryTerm } : {}),
    };
  }, [queryTerm]);

  // Convert DataGrid sort model to GraphQL orderByV2
  const orderByV2 = useMemo(() => {
    if (sortModel.length === 0) {
      return {
        field: PolicyAnalyticsOrderField.Title,
        direction: OrderDirection.Asc,
      };
    }
    const { field, sort } = sortModel[0];
    return {
      field: SORT_FIELD_MAP[field] || PolicyAnalyticsOrderField.Title,
      direction: sort === "desc" ? OrderDirection.Desc : OrderDirection.Asc,
    };
  }, [sortModel]);

  // Initialize pagination with undefined - will be updated after query
  const pagination = useDataGridPagination({
    prefix: "policies",
    defaultPageSize: 10,
    pageInfo: undefined,
    totalCount: undefined,
    resetDeps: [filterVariables, orderByV2.field, orderByV2.direction],
  });

  const queryVariables: GetPlatformPoliciesQueryVariables = useMemo(() => {
    return {
      input: {
        ...filterVariables,
        orderByV2,
      },
      first: pagination.first,
      after: pagination.after,
    };
  }, [filterVariables, orderByV2, pagination.first, pagination.after]);

  const { data, loading } = useGetPlatformPoliciesQuery({
    variables: queryVariables,
    fetchPolicy: "cache-and-network",
  });

  // Get pagination with actual data - this will update rowCount properly
  const paginationWithData = useDataGridPagination({
    prefix: "policies",
    defaultPageSize: 10,
    pageInfo: data?.policyAnalytics?.pageInfo,
    totalCount: data?.policyAnalytics?.totalCount,
    resetDeps: [filterVariables, orderByV2.field, orderByV2.direction],
  });

  const rows: GridRowsProp<PlatformPoliciesRow> = useMemo(() => {
    const platformPoliciesNodes =
      data?.policyAnalytics?.edges?.flatMap((edge) => edge.node ?? []) ?? [];

    return platformPoliciesNodes.map((node) => {
      const score = node.score;
      const assets = score?.blastRadius?.assets ?? 0;
      const policyCompliance = score?.completion ?? null;
      return {
        id: PolicyMrnToId(node.mrn),
        encodedId: PolicyMrnToURIEncodedId(node.mrn),
        mrn: node.mrn,
        name: node.name,
        icon: node.icon,
        action: node.action,
        assets,
        policyCompliance,
        findingStatsPass: score?.findingStats?.pass ?? null,
        findingStatsTotal: score?.findingStats?.total ?? null,
      };
    });
  }, [data]);

  const onSortModelChange = useCallback((newModel: GridSortModel) => {
    setSortModel(newModel.length > 0 ? newModel : DEFAULT_SORT_MODEL);
  }, []);

  const tableProps = {
    columns,
    rows,
    loading,
    paginationMode: "server" as const,
    sortingMode: "server" as const,
    paginationModel: paginationWithData.paginationModel,
    onPaginationModelChange: paginationWithData.onPaginationModelChange,
    rowCount: paginationWithData.rowCount,
    sortModel,
    onSortModelChange,
    pageSizeOptions: [10, 25, 50] as const,
  };

  return {
    tableProps,
  };
}
